import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import {
	getScrollHeight,
	getOffsetHeight,
} from '@sparefoot/react-core/utils/dom';

import './Expand.scss';

// Expand / Collapse Animation Component
const Expand = ({
	showing,
	children,
	showTeaser = false,
	preserve = false,
}) => {
	const [expanded, setExpanded] = useState(!!showing);
	const [height, setHeight] = useState(showing ? null : 0);
	const [overflow, setOverflow] = useState('hidden');

	const instanceRef = useRef(null);
	const teaserHeightRef = useRef(null);
	const prevShowingRef = useRef(showing);

	const enter = useCallback(() => {
		setExpanded(true);
		setHeight(teaserHeightRef.current || 0);
		setOverflow('hidden');

		// animate
		setTimeout(() => {
			setHeight(getScrollHeight(instanceRef.current));
		}, 0);

		// clear
		setTimeout(() => {
			setHeight(null);
			setOverflow(null);
		}, 350);
	}, []);

	const exit = useCallback(() => {
		setHeight(getScrollHeight(instanceRef.current));
		setOverflow('hidden');

		// animate
		setTimeout(() => {
			setHeight(teaserHeightRef.current || 0);
		}, 0);

		// clear
		setTimeout(() => {
			setExpanded(false);
		}, 350);
	}, []);

	// Respond to showing prop changes (replaces UNSAFE_componentWillReceiveProps)
	useEffect(() => {
		if (!prevShowingRef.current && showing) {
			enter();
		} else if (prevShowingRef.current && !showing) {
			exit();
		}
		prevShowingRef.current = showing;
	}, [showing, enter, exit]);

	const bindRef = useCallback(
		(ref) => {
			instanceRef.current = ref;
			if (ref && showTeaser && !showing) {
				teaserHeightRef.current = getOffsetHeight(ref);
				setHeight(teaserHeightRef.current);
			}
		},
		[showTeaser, showing],
	);

	return preserve || expanded || showTeaser
		? React.cloneElement(children, {
				ref: bindRef,
				style: {
					overflow,
					transition: 'height 250ms ease-out',
					height,
				},
				className: classnames(
					children.props.className,
					'expand-content',
					{
						collapsed: showTeaser && !expanded,
						expanded: showTeaser && expanded,
						'preserve-collapsed': preserve && !expanded,
					},
				),
			})
		: null;
};

Expand.propTypes = {
	showing: PropTypes.bool,
	children: PropTypes.node,
	/** Shows the initial offset height of the child container instead of collapsing it */
	showTeaser: PropTypes.bool,
	preserve: PropTypes.bool,
};

export default Expand;
